<template>
  <div
    :class="[
      { 'full-height--adjust': activeTab === 0  },
      { 'full-height--partil-adjust': activeTab > 0 }
    ]"
    style="min-height: 3px"
  >
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <div v-if="isValidArray(items)" class="table">
          <v-layout class="table__header pl-0" align-center>
            <v-flex class="td--action" xs1 />
            <v-flex style="min-width: 130px;">ID</v-flex>
            <v-flex xs2>{{ $t(`${namespace}.date`) }}</v-flex>
            <v-flex xs3>{{ $t(`${namespace}.employeeName`) }}</v-flex>
            <v-flex xs5>{{ $t(`${namespace}.justification`) }}</v-flex>
            <v-flex xs1>{{ $t(`${namespace}.value`) }}</v-flex>
          </v-layout>

          <div v-if="!summarize" class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong>&nbsp;<span class="text--lowercase">{{ $t(`${namespace}.ridesThisMonth`) }}</span>
          </div>

          <div v-if="summarize && summarizeNumberCount && summarizeValue" class="table__summarize-status table__summarize-status--success">
            <strong>{{ summarizeNumberCount | formatNumber }}</strong> {{ $t(`${namespace}.summarizeCreditText`) }} <strong class="success--text">{{ summarizeValue | currencyBRL }}</strong>
          </div>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile
                v-if="
                  item.credit && item.credit.value &&
                  item.ride && item.ride.amount
                "
                class="table__body__tr pl-0"
                :class="[
                  { 'table__body__tr--partial': Number(item.credit.value) < Number(item.ride.amount) },
                  { 'table__body__tr--total': Number(item.credit.value) === Number(item.ride.amount) }
                ]"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex class="td--action table__td--prevent text-xs-center" xs1>
                        <v-menu
                          v-if="hasRole('billing', 'ROLE_PUT_UPDATE_CREDIT') || hasRole('billing', 'ROLE_DELETE_CANCEL_CREDIT')"
                          offset-y
                          right
                        >
                          <button class="table-body__actions" slot="activator">
                            <v-icon>more_vertical</v-icon>
                          </button>

                          <v-list class="dropdown-menu-actions__list">
                            <v-list-tile
                              v-if="
                                item.ride &&
                                item.ride.detail &&
                                item.ride.detail.apiInfo &&
                                item.ride.detail.apiInfo.apiTaxiJobId &&
                                hasRole('billing', 'ROLE_PUT_UPDATE_CREDIT')
                              "
                              :to="{
                                name: 'RideCredit',
                                params: {
                                  type: 'editar',
                                  id: item.ride.detail.apiInfo.apiTaxiJobId
                                }
                              }"
                            >
                              <v-list-tile-title>{{ $t(`${namespace}.editRideCredit`) }}</v-list-tile-title>
                            </v-list-tile>

                            <v-divider
                              v-if="
                                item.ride &&
                                item.ride.detail &&
                                item.ride.detail.apiInfo &&
                                item.ride.detail.apiInfo.apiTaxiJobId &&
                                hasRole('billing', 'ROLE_PUT_UPDATE_CREDIT') &&
                                hasRole('billing', 'ROLE_DELETE_CANCEL_CREDIT')
                              "
                            />

                            <v-list-tile
                              v-if="hasRole('billing', 'ROLE_DELETE_CANCEL_CREDIT')"
                              @click="handleDeleteCredit(item)"
                            >
                              <v-list-tile-title>{{ $t(`${namespace}.deleteRideCredit`) }}</v-list-tile-title>
                            </v-list-tile>
                          </v-list>
                        </v-menu>
                      </v-flex>

                      <v-flex style="min-width: 130px;">
                        <span class="text--ellipsis" contenteditable>
                          <span
                            v-if="
                              item.ride &&
                              item.ride.detail &&
                              item.ride.detail.apiInfo &&
                              item.ride.detail.apiInfo.apiTaxiJobId
                            "
                          >
                            {{ item.ride.detail.apiInfo.apiTaxiJobId }}
                          </span>
                          <span v-else>-</span>

                          <br>

                          <span
                            v-if="
                              item.credit && item.credit.value &&
                              item.ride && item.ride.amount
                            "
                          >
                            <strong
                              v-if="Number(item.credit.value) < Number(item.ride.amount)"
                              :title="$t(`${namespace}.partialCredit`)"
                              class="chargeback__text text--ellipsis"
                            >
                              {{ $t(`${namespace}.partialCredit`) }}
                            </strong>

                            <strong
                              v-if="Number(item.credit.value) === Number(item.ride.amount)"
                              :title="$t(`${namespace}.totalCredit`)"
                              class="chargeback__text text--ellipsis"
                            >
                              {{ $t(`${namespace}.totalCredit`) }}
                            </strong>
                          </span>
                          <span v-else>-</span>
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span v-if="item.credit && item.credit.createdDate">
                          <span
                            :title="item.credit.createdDate | formatDate('DD/MM/YYYY')"
                            class="text--ellipsis"
                          >
                            {{ item.credit.createdDate | formatDate('DD/MM/YYYY') }}
                          </span>

                          <span class="gray-6--text caption text--ellipsis">
                            {{ $t(`${namespace}.to`) }} {{ item.credit.createdDate | formatDate('HH:mm') }}
                          </span>
                        </span>

                        <span v-else>-</span>
                      </v-flex>

                      <v-flex xs3>
                        <span class="text--ellipsis">
                          {{ $t(`${namespace}.per`) }} <span class="text--capitalize">
                            <span
                              v-if="item.credit && item.credit.createdBy && item.credit.createdBy.name"
                              :title="item.credit.createdBy.name"
                            >
                              {{ item.credit.createdBy.name | ellipsis(25) }}
                            </span>
                            <span v-else>-</span>
                          </span>
                        </span>
                      </v-flex>

                      <v-flex xs5>
                        <div class="pr-3 break-line">
                          <span
                            v-if="item.credit && item.credit.reason"
                            :title="item.credit.reason"
                          >
                            {{ item.credit.reason | ellipsis(100) }}
                          </span>
                          <span v-else>-</span>
                        </div>
                      </v-flex>

                      <v-flex xs1>
                        <span
                          v-if="item.credit && item.credit.value"
                          :title="item.credit.value | currencyBRL"
                          class="success--text"
                        >
                          {{ item.credit.value | currencyBRL }}
                        </span>
                        <span v-else>-</span>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="usage-policy"
          :text="$t(`${namespace}.creditsEmptyState`)"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isArray, formatNumber, hasRole } from 'utils/functions'
import EmptyState from 'components/EmptyState'

export default {
  name: 'TableCredit',
  props: {
    items: {
      type: Array,
      required: true
    },
    summarizeNumberCount: {
      type: Number,
      default: 0
    },
    summarizeValue: {
      type: Number,
      default: 0
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    summarize: {
      type: Boolean,
      default: false
    },
    activeTab: {
      type: Number
    }
  },
  components: {
    EmptyState
  },
  computed: {
    routeId () {
      const { id } = this.$route.params
      return id ? Number(id) : null
    },

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.FinancialAdjustmentsPage'
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    hasRole (group, role) {
      return hasRole(group, role)
    },

    isValidArray (val) {
      return isArray(val)
    },

    handlePage (page) {
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    handleDeleteCredit (item) {
      this.$emit('deleteCredit', item)
    }
  }
}
</script>

<style lang="scss" scoped>
.full-height--partil-adjust {
  height: calc(100% - 69px);
}

.full-height--adjust {
  height: calc(100% - 69px - 64px);
}

.table__title {
  align-items: center;
  border-bottom: 1px solid rgba(0,0,0,0.12);
  display: flex;
  font-size: 14px;
  font-weight: 600;
  padding: 0 16px;
  height: 40px;
}
</style>

<style lang="scss">
.credit {
  /* border-top: 1px solid $gray-3; */
  height: 32px;
  padding-right: 16px;
  position: relative;

  &:before,
  &:after {
    content: '';
    position: absolute;
  }

  &:before {
    border-bottom: 8px solid #eee;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid transparent;
    bottom: 100%;
    display: block;
    height: 10px;
    left: 18px;
    width: 10px;
  }
}

.chargeback__text {
  font-size: 12px;
}

.line-through {
  color: $gray-4;
  text-decoration:line-through;
}

.table__summarize-status {
  border-bottom: 1px solid $gray-4;
  text-align: center;
  padding: 20px 0;

  &.table__summarize-status--success {
    background-color: #fff;
  }
}

.table__body__tr--partial {
  background-color: #f3fef7;

  &:hover {
    background-color: darken(#f3fef7, 5);
  }
}

.table__body__tr--total {
  background-color: #b2ffcf;

  &:hover {
    background-color: darken(#b2ffcf, 5);
  }
}
</style>
